import { Component, EventEmitter, inject, Input, OnChanges, OnInit, Output, Signal, SimpleChanges } from '@angular/core';
import { UIIconsComponent } from '../../ui/icons/icons.component';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { UISkeletonChatCopilotComponent } from '../../ui/skeletons/chat-copilot/skeleton-chat-copilot.component';
import { UserService } from 'src/app/core/services/user.service';
import { LoaderComponent } from '../../common-ui-components/loader/loader.component';
import { AgGridModule } from 'ag-grid-angular';
import { MarkdownModule } from 'ngx-markdown';
import { DataCopilotApiService } from 'src/app/core/http/services/data-copilot.service';
import { CardItem } from '@interface/card.interface';

@Component({
  selector: 'app-copilot-modal',
  standalone: true,
  providers: [DatePipe],
  imports: [
    UIIconsComponent, 
    CommonModule,
    FormsModule, 
      UISkeletonChatCopilotComponent, 
      LoaderComponent,
      AgGridModule,
      MarkdownModule
     ],
  templateUrl: './copilot-modal.component.html',
  styleUrl: './copilot-modal.component.scss'
})
export class CopilotModalComponent implements OnInit, OnChanges {   
  private userService = inject(UserService);
  private dataCopilotApiService = inject(DataCopilotApiService);
  private datePipe = inject(DatePipe);
  @Input() selectedScope = new Input();
  @Input() templateId = new Input();
  @Input() reportData = new Input();
  @Input() isSavedReport = new Input();
  @Input() conversations: any = new Input();
  @Input() reportDetails: any = new Input();
@Input() canvasId: string = '';
@Input() assetModelId: string = '';
@Input() type = ''; // 'CANVAS' or 'REPORT'
@Input() selectedReportIds: string[] = [];
@Input() selectedAnswerIds: string[] = [];
@Input() cards: CardItem[] = [];
@Input() handleApiInParent: boolean = false;
@Output() emitConversations: EventEmitter<any> = new EventEmitter<any>();
@Output() askQuestion: EventEmitter<string> = new EventEmitter<string>();
  public isAnswerLoading = false;
  public questionsAndAnswersLoading = false;
  public isLoadingData = false;
  public messageText = '';
  defaultColDef = { flex: 1, minWidth: 100, resizable: true, filter: true, sortable: true };
  answers: {
    isLoading: boolean;
    text: string;
    type: String;
    timestamp: any;
    tableData?: any;
    warning?: boolean;
    insights?: string | null;
    showInsights?: boolean;
    loadingInsights?: boolean;
  }[] = [];
  questions: any[] = [];
  firstMessage = false;
  isScrollToBottom = false;
  userPhoto: Signal<string> = this.userService.select('photo');

  ngOnInit(): void {
  }

  ngOnChanges(changes: SimpleChanges): void {
      if(changes['conversations']){
        this.questions = this.conversations.questions ?? [];
        this.answers = this.conversations.answers ?? [];
        const lastAnswer = this.answers[this.answers.length - 1];
        if (lastAnswer && lastAnswer.isLoading) {
            this.isAnswerLoading = true;
        } else {
            this.isAnswerLoading = false;
        }
      }
  }

  sendMessage() {
    // For canvas, we don't need a selectedScope
    if (this.messageText.trim() && (this.selectedScope || this.type === 'CANVAS')) {
      if (this.handleApiInParent) {
        this.askQuestion.emit(this.messageText);
        this.messageText = '';
        this.isScrollToBottom = true;
      } else {
        this.isAnswerLoading = true;
        this.sendMessageToExistingConversation();
      }
    }
    this.isScrollToBottom = true;
  }

  private sendMessageToExistingConversation(): void {
    const questionAsset = {
      parameters: { INPUT_TEXT: this.messageText },
    };
    this.questions = [...this.questions, questionAsset];
    const messageContent = this.messageText;
    this.messageText = '';
    this.nlpAPI(messageContent);
  }

  openTermsModal(event: Event): void {
    event.preventDefault();
    window.open('https://www.anylytics.ai/terms-of-use', '_blank');
  }

  openPrivacyModal(event: Event): void {
    event.preventDefault();
    window.open('https://www.anylytics.ai/privacy-policy', '_blank');
  }



  public nlpAPI(messageText: string, callback?: () => void): void {
    const answerIndex = this.answers.length;
    const questionIndex = this.questions.length - 1;
    this.answers.push({
      isLoading: true,
      text: '',
      type: '',
      timestamp: '',
      tableData: null,
    });
  
    const payload = this.getPayload(messageText);
 
    const currentTime = this.datePipe.transform(new Date(), 'MMMM d, YYYY, h:mm a');
  
    // Use canvasChat if canvasId is provided (including empty string), otherwise use reportChat
    const apiCall = this.canvasId !== undefined && this.canvasId !== null && this.canvasId.trim() !== ''
      ? this.dataCopilotApiService.canvasChat(payload)
      : this.dataCopilotApiService.reportChat(payload);
      
    apiCall.subscribe({
      next: (data) => {
        const questionData = data.data.find((item: any) => item.type === 'QUESTION');
        const answerData = data.data.find((item: any) => item.type === 'ANSWER');
        const reportData = data.data.find((item: any) => item.type === 'REPORT');
        
        this.questions[questionIndex] = {
          id: questionData?.id || '0',
          parameters: {
            INPUT_TEXT: messageText
          },
        };
  
        if (!answerData || !answerData.parameters.DATA) {
          this.answers[answerIndex] = {
            isLoading: false,
            text: `⚠️ We could not generate an answer for this question, please try again later!`,
            type: 'simple',
            timestamp: currentTime,
            tableData: null,
            warning: true,
          };
        } else {
          this.answers[answerIndex] = {
            isLoading: false,
            text: answerData.parameters.DATA,
            type: 'simple',
            timestamp: currentTime,
            tableData: null,
          };
        }
  
        this.isAnswerLoading = false;
        this.questionsAndAnswersLoading = false;
        this.firstMessage = false;

        this.emitConversations.emit({
            questions: this.questions,
            answers: this.answers,
            reportAsset: reportData
          });
        if (callback) callback();
      },
      error: (err) => {
        this.answers[answerIndex] = {
          isLoading: false,
          warning: true,
          text: '⚠️ We could not generate an answer for this question, please try again later!',
          type: 'simple',
          timestamp: currentTime,
          tableData: null,
        };
        this.isAnswerLoading = false;
        this.questionsAndAnswersLoading = false;
        this.firstMessage = false;
        this.emitConversations.emit({
          questions: this.questions,
          answers: this.answers,
          reportAsset: null
        });
      },
    });
  }    

  getPayload(messageText: string): any {
    const buildCardDataMapping = (reportIds: string[], answerIds: string[]) => {
      const reportDataMap: { [key: string]: any[] } = {};
      const answerDataMap: { [key: string]: any[] } = {};

      if (this.cards && this.cards.length > 0) {
        this.cards.forEach(card => {
          if (card.reportId && card.data?.reportVisualData) {
            reportDataMap[card.reportId] = card.data.reportVisualData;
          }
          if (card.answerId && card.data?.answerData) {
            answerDataMap[card.answerId] = card.data.answerData;
          }
        });
      }

      return {
        reportDataMap: Object.keys(reportDataMap).length > 0 ? reportDataMap : undefined,
        answerDataMap: Object.keys(answerDataMap).length > 0 ? answerDataMap : undefined
      };
    };

    if (this.type === 'CANVAS') {
      let reportIds: string[] = [];
      let answerIds: string[] = [];

      if (this.canvasId) {
        reportIds = this.selectedReportIds;
        answerIds = this.selectedAnswerIds;
      } else {
        if (this.selectedReportIds.length > 0 || this.selectedAnswerIds.length > 0) {
          reportIds = this.selectedReportIds;
          answerIds = this.selectedAnswerIds;
        } 
        // If no selections, get all report and answer IDs from cards
        else if (this.cards && this.cards.length > 0) {
          reportIds = this.cards
            .filter(card => card.reportId)
            .map(card => card.reportId as string);
          
          answerIds = this.cards
            .filter(card => card.answerId)
            .map(card => card.answerId as string);
        }
      }

      const cardDataMapping = buildCardDataMapping(reportIds, answerIds);
      console.log(cardDataMapping)
      return {
        query: messageText,
        canvasId: this.canvasId,
        selectedReportIds: reportIds,
        selectedAnswerIds: answerIds,
        ...(cardDataMapping.reportDataMap && { reportDataMap: cardDataMapping.reportDataMap }),
        ...(cardDataMapping.answerDataMap && { answerDataMap: cardDataMapping.answerDataMap })
      };
    } 
    else if (this.isSavedReport) {
      return {
        query: messageText,
        reportId: this.reportDetails.id,
        reportData: this.reportData
      };
    } else {
      return {
        query: messageText,
        reportAsset: {
          id: "0",
          name: this.reportDetails.name || "Temporary Report",
          description: this.reportDetails.description || "Temporary Report Analysis",
          type: "REPORT",
          status: "OPEN",
          parameters: {
            ...this.reportDetails.parameters,
            CONVERSATION_MESSAGES: this.conversations?.reportAsset?.parameters?.CONVERSATION_MESSAGES || []
          },
          creator: "user@example.com"
        },
        reportData: this.reportData
      };
    }
  }
}
